import {
  type LessonItem,
  type SchoolEvent,
  type ScheduleScope,
  addDays,
  shortName,
  timeOf,
  todayIso,
  useLessons,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import type { TFunction } from 'i18next';
import { type Grad, SUBJECT_GRAD } from '../design/forms';
import { useMinute } from '../lib/now';
import { roomOf } from '../student/home/model';
import { EVENT_LOOK } from '../student/home/GlassDay';
import { EVENT_GRAD } from '../student/home/ShapesDay';
import { type PlanItem, type PlanMark, type PlanNote, minutesOf } from './Planner';

/** an event with a time but no end stands on the planner for an hour */
const EVENT_MINUTES = 60;

/** Whose lesson it is on the planner: what its block says under the subject, what the chips group. */
export type Perspective = 'student' | 'teacher' | 'class';

/** The teacher's classes each in a colour of their own (a teacher's lessons are mostly one subject). */
const CLASS_TONES: ReadonlyArray<Grad> = [
  'sky',
  'lavender',
  'mint',
  'apricot',
  'pink',
  'butter',
  'aqua',
  'coral',
  'lime',
  'cobalt',
];
/** The colours of a teacher's classes: by their order (7-Б, 9-А, 10-А…), no two alike. */
export function classTones(labels: Iterable<string>): (label: string) => Grad {
  const order = [...new Set(labels)].sort((a, b) => a.localeCompare(b, 'uk', { numeric: true }));
  return (label) => CLASS_TONES[Math.max(0, order.indexOf(label)) % CLASS_TONES.length] ?? 'sky';
}

/** A lesson as a block of the planner (the teacher's: the class first, in the class's colour). */
export function lessonPlan(
  t: TFunction,
  l: LessonItem,
  perspective: Perspective,
  onOpen?: (el: HTMLElement) => void,
  /** the teacher's: the colour of the lesson's class */
  classTone?: (label: string) => Grad,
): PlanItem {
  const look = subjectLook(l.subject);
  const teacher = l.substitute ?? l.teachers[0];
  const marks: PlanMark[] = [];
  if (l.isTest) marks.push('test');
  if (l.substitute) marks.push('substitute');
  if (l.format === 'online') marks.push('online');
  if (l.format === 'shelter') marks.push('shelter');
  const room = roomOf(t, l.room);
  return {
    id: l.id,
    date: l.date,
    start: minutesOf(timeOf(l.startsAt)),
    end: minutesOf(timeOf(l.endsAt)),
    title: perspective === 'teacher' ? `${l.classLabel} · ${l.subject}` : l.subject,
    sub:
      perspective === 'teacher'
        ? [l.groupLabel, room].filter(Boolean).join(' · ')
        : [room, teacher && shortName(teacher)].filter(Boolean).join(' · '),
    tone: classTone ? classTone(l.classLabel) : SUBJECT_GRAD[look.tone],
    group: perspective === 'teacher' ? l.classLabel : l.subject,
    marks,
    cancelled: l.status === 'cancelled',
    icon: look.icon,
    ...(onOpen && { onOpen }),
  };
}

/**
 * The school's events of the range: one with a time — a block in its hour on each of its days, as a
 * lesson is (his «почему они вверху узкой плашкой, а не в своём времени», 28.10); one without — a note
 * of its days over the grid; the holidays tint their days.
 */
export function eventsPlan(
  t: TFunction,
  events: ReadonlyArray<SchoolEvent>,
  range: { from: string; to: string },
  onOpen?: () => void,
): { items: PlanItem[]; notes: PlanNote[] } {
  const items: PlanItem[] = [];
  const notes: PlanNote[] = [];
  for (const e of events) {
    if (e.startsOn > range.to || e.endsOn < range.from) continue;
    if (e.kind === 'holiday' || !e.atTime) {
      notes.push({
        id: e.id,
        startsOn: e.startsOn,
        endsOn: e.endsOn,
        title: e.title,
        tone: e.kind === 'holiday' ? 'mint' : EVENT_GRAD[e.kind],
        holiday: e.kind === 'holiday',
        ...(onOpen && e.kind !== 'holiday' && { onOpen }),
      });
      continue;
    }
    const start = minutesOf(e.atTime);
    for (
      let d = e.startsOn < range.from ? range.from : e.startsOn;
      d <= e.endsOn && d <= range.to;
      d = addDays(d, 1)
    ) {
      items.push({
        id: `event-${e.id}-${d}`,
        date: d,
        start,
        end: start + EVENT_MINUTES,
        title: e.title,
        sub: e.place ?? t('schedule.event'),
        tone: EVENT_GRAD[e.kind],
        marks: [],
        event: e.kind,
        badge: EVENT_LOOK[e.kind].icon,
        ...(onOpen && { onOpen: () => onOpen() }),
      });
    }
  }
  return { items, notes };
}

/**
 * The day the planner opens on: the first day ahead that still has a lesson (today while its lessons
 * go on) — on the holidays, the week the lessons start again. `ready` once it is known.
 */
export function useFirstLessonDay(schoolId: string, scope: ScheduleScope) {
  const today = todayIso();
  const now = useMinute();
  const lessons = useLessons(schoolId, scope, today, addDays(today, 28));
  const next = (lessons.data ?? []).find(
    (l) => l.status !== 'cancelled' && (l.date > today || Date.parse(l.endsAt) > now),
  );
  return { date: next?.date, ready: !lessons.isPending };
}
